/**
 * The listings filter SET — the one app-side filter currency.
 *
 * The SSR loader decodes the URL through the shared codec
 * (`@letsgethome/routes` `decodeListingsFilterParams`) exactly once and hands
 * the result to BOTH projections:
 *
 *   URL ──decode──▶ ListingsFilterSet ──┬─▶ toProtoFilters()  (SSR RPC, mappers/storefront/listings-filters.ts)
 *                                       └─▶ toBffFilters()    (client refetch, this file)
 *
 * Before this seam existed the client half simply never received the set (the
 * pages built `<ListingsClient>` without the prop), so panning the map after a
 * filtered load re-queried by BOUNDS ONLY — silently dropping the user's
 * district/estate/price/bedroom selection — and the filter badge counted zero
 * forever. The set is therefore a REQUIRED prop, not an optional one: a page
 * that forgets it no longer compiles.
 *
 * Values stay string-level (the codec's decoded shape) all the way to the
 * client because this crosses the SSR loader-data boundary, which is JSON —
 * a `bigint` cents field here would throw on serialization. The typed
 * conversion happens at each projection's own seam.
 *
 * ★ And since R4-2 the BFF projection carries that invariant ONTO THE WIRE:
 * `toBffFilters` mints `ExactInt` (the int64's decimal digits), never a
 * `bigint`, because `bff.ts` serialises the request body with a bare
 * `JSON.stringify` and that is the one JSON exit no `JsonSafe` guard used to
 * sit on. The module header above had stated the invariant since the seam was
 * written; the code now matches it. `toProtoFilters` — the SSR half, which
 * builds a proto `Money` and never crosses JSON — is unchanged.
 */
import type { MapBounds } from '@/shared/lib/geo/bounds';
import { asPropertyTypeName } from '@/shared/lib/types/listing';
import type { SearchListingsParams } from '@/shared/lib/types/search';
import { activeFilterCount, priceFilterBound, roomFilterMin } from '@letsgethome/lib/wasm';
import { BEDROOMS_STUDIO_TOKEN, type DecodedListingsFilterParams } from '@letsgethome/routes';
import { exactInt, type ExactInt } from '@letsgethome/types/branded';
import type { DistrictSlug } from '@letsgethome/types/districts';

/**
 * The decoded listings filter set, plus the district FACET scope.
 *
 * `districtSlug` is not a URL filter param — it comes from the
 * `/districts/:slug/{rent,buy}` path segment — but it is a discrete location
 * on the wire (`SearchFilters.district_slug`), so it belongs to the same set:
 * it participates in the bounds XOR below and must survive a client refetch
 * exactly like `districtId` does.
 */
export interface ListingsFilterSet extends DecodedListingsFilterParams {
  districtSlug?: DistrictSlug;
}

/** An integer param, or undefined when absent/malformed. */
function int(raw: string | undefined): number | undefined {
  if (raw === undefined) return undefined;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * A bedrooms/bathrooms MINIMUM, or undefined. The "0 = any" unset decision is
 * the Rust filters SSOT's (`roomFilterMin`) — never re-derived here.
 */
function rooms(raw: string | undefined): number | undefined {
  const n = int(raw);
  return n === undefined ? undefined : roomFilterMin(n);
}

/**
 * The BEDROOMS filter from its URL value (D11): `studio` → `0` (a studio
 * EXACTLY — the wire's present 0); a number → a minimum through the Rust
 * filters SSOT (`roomFilterMin`, so a legacy `?bedrooms=0` stays "any");
 * anything else → undefined. The server's twin is
 * `domain_listing_facts::bedrooms_filter`. Both projections (the SSR proto and
 * the BFF body) and the badge read this one decode.
 */
export function bedroomsFilterParam(raw: string | undefined): number | undefined {
  return raw === BEDROOMS_STUDIO_TOKEN ? 0 : rooms(raw);
}

/**
 * A `*_price` cents param → the wire's exact-int carrier, or undefined. The
 * set/unset decision (0 = any) is the Rust filters SSOT's (`priceFilterBound`).
 *
 * The `BigInt(raw)` parse STAYS, and so does the `catch`: that throw is what
 * decides a malformed param is "no filter" rather than `NaN` on the wire, and
 * minting from the parsed `bigint` is what makes the result an `ExactInt`
 * honestly (the brand's only mint takes a `bigint`). What R4-2 changed is the
 * MINT alone — `exactInt(value)` where it was `centsBigInt(value)` — because
 * the value returned here lands on a BFF request body that `JSON.stringify`
 * has to encode, and it cannot encode a `bigint`.
 */
function cents(raw: string | undefined): ExactInt | undefined {
  if (raw === undefined) return undefined;
  let value: bigint;
  try {
    value = BigInt(raw);
  } catch {
    return undefined;
  }
  return priceFilterBound(Number(value)) !== undefined ? exactInt(value) : undefined;
}

/**
 * Whether the set names a DISCRETE location (a district, a district facet, or
 * an estate).
 *
 * The wire forbids combining one with a map viewport — CEL
 * `search.filters.bounds_xor_discrete_location`. Both projections consult this
 * one predicate so the SSR request and the client refetch cannot disagree
 * about which of the two wins (they must, or a map pan on a district-scoped
 * page would build a request the backend rejects outright).
 */
export function hasDiscreteLocation(set: ListingsFilterSet): boolean {
  return Boolean(set.districtId ?? set.districtSlug ?? set.estateId);
}

/**
 * Resolve the map viewport against the set under the wire's XOR rule: a
 * discrete location always wins, so the viewport is honoured only when the
 * user has not pinned a district/estate.
 */
export function resolveViewport(
  set: ListingsFilterSet,
  viewport: MapBounds | undefined
): MapBounds | undefined {
  return hasDiscreteLocation(set) ? undefined : viewport;
}

/**
 * Project the set onto the `/api/search/listings` BFF filter shape, folding in
 * the current map viewport under the XOR rule. Returns `undefined` when
 * nothing is filtered, so a bare `/listings/rent` sends no filters at all.
 */
export function toBffFilters(
  set: ListingsFilterSet,
  viewport?: MapBounds
): SearchListingsParams['filters'] {
  const bounds = resolveViewport(set, viewport);
  const filters: NonNullable<SearchListingsParams['filters']> = {};

  if (bounds) filters.bounds = bounds;
  if (set.districtId) filters.districtId = set.districtId;
  if (set.districtSlug) filters.districtSlug = set.districtSlug;
  if (set.estateId) filters.estateId = set.estateId;

  const minPriceCents = cents(set.minPrice);
  const maxPriceCents = cents(set.maxPrice);
  if (minPriceCents !== undefined) filters.minPriceCents = minPriceCents;
  if (maxPriceCents !== undefined) filters.maxPriceCents = maxPriceCents;

  const bedrooms = bedroomsFilterParam(set.bedrooms);
  const bathrooms = rooms(set.bathrooms);
  if (bedrooms !== undefined) filters.bedrooms = bedrooms;
  if (bathrooms !== undefined) filters.bathrooms = bathrooms;

  const query = set.query?.trim() || undefined;
  if (query) filters.query = query;

  // Only the literal `true` sets it — the wire's `false` IS "no pets filter".
  if (set.petsAllowed === 'true') filters.petsAllowed = true;
  const propertyType = asPropertyTypeName(set.propertyType);
  if (propertyType) filters.propertyType = propertyType;

  return Object.keys(filters).length > 0 ? filters : undefined;
}

/**
 * The SSOT's rooms argument for a bedrooms filter VALUE. `active_filter_count`
 * counts a positive minimum; a studio filter (`0`) is set too, so it is
 * handed on as a presence — the wasm rail has no room for an `Option` today
 * (`domain-wasm/src/lib.rs` is at its file ceiling), which is the follow-up
 * that moves this decision into the SSOT.
 */
function roomsCountArg(value: number | null | undefined): number {
  if (value === null || value === undefined) return 0;
  return Math.max(value, 1);
}

/**
 * The count of REFINEMENT filters only (price range, rooms, pets) — what the
 * filters modal's reset affordance keys off.
 */
export function refinementFilterCount(refinements: {
  // `bigint | null`, and deliberately NOT `ExactInt`: this is the modal's own
  // local state, which never crosses a JSON boundary — the count is computed
  // and rendered in the same browser. Only what reaches a wire is `ExactInt`.
  minPriceCents: bigint | null;
  maxPriceCents: bigint | null;
  bedrooms: number | null;
  bathrooms: number | null;
  petsAllowed: boolean;
}): number {
  return activeFilterCount(
    refinements.minPriceCents !== null,
    refinements.maxPriceCents !== null,
    roomsCountArg(refinements.bedrooms),
    refinements.bathrooms ?? 0,
    refinements.petsAllowed,
    // The browsing CONTEXT (district/estate/query) is deliberately excluded:
    // this count drives the filters modal's "reset" affordance, and reset
    // clears refinements only — the partition is the Rust SSOT's
    // (`filter_field_clears_on_clear_all`), not this call site's.
    false,
    false,
    false
  );
}

/**
 * The number the filter badge shows — the Rust filters SSOT's count
 * (`domain_core::filters::active_filter_count`), reached through the one
 * named-argument adapter so no call site writes the wasm positional form.
 *
 * The badge previously hand-rolled this and omitted the district, estate and
 * query dimensions — the same drift the SSOT was created to settle when
 * Android and iOS disagreed.
 */
export function filterBadgeCount(set: ListingsFilterSet): number {
  return activeFilterCount(
    cents(set.minPrice) !== undefined,
    cents(set.maxPrice) !== undefined,
    roomsCountArg(bedroomsFilterParam(set.bedrooms)),
    rooms(set.bathrooms) ?? 0,
    set.petsAllowed === 'true',
    Boolean(set.districtId ?? set.districtSlug),
    Boolean(set.estateId),
    Boolean(set.query?.trim())
  );
}
